import { useState } from "react";
import { Camera } from "lucide-react";
import { gallery } from "../mock";
import { useLang } from "../i18n";

const spans = ["row-span-2", "", "", "row-span-2", "", "row-span-2", "", "", ""];
const marqueeWords = ["Lasagna classica, sempre sia lodata", "Espresso Martini, prega per noi", "Pinsa romana, ora pro nobis", "Pasta all'ubriaca, per i più bisognosi"];

/** Build possible public URLs for a gallery filename (handles .jpeg/.jpg and casing). */
function gallerySrcCandidates(fileName) {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const clean = String(fileName).replace(/^\//, "");
  const lower = clean.toLowerCase();
  const asJpg = clean.replace(/\.jpeg$/i, ".jpg");
  const asJpeg = clean.replace(/\.jpg$/i, ".jpeg");
  return [
    `${base}/${clean}`,
    `${base}/${lower}`,
    `${base}/${asJpg}`,
    `${base}/${asJpeg}`,
    `${base}/${asJpg.toLowerCase()}`,
    `${base}/${asJpeg.toLowerCase()}`,
  ].filter((v, i, arr) => arr.indexOf(v) === i);
}

function GalleryImage({ fileName, alt, className }) {
  const candidates = gallerySrcCandidates(fileName);
  const [index, setIndex] = useState(0);

  return (
    <img
      src={candidates[index]}
      alt={alt}
      className={className}
      onError={() => {
        if (index < candidates.length - 1) setIndex(index + 1);
      }}
    />
  );
}

export default function Gallery() {
  const { t } = useLang();
  return (
    <section id="gallery" className="relative py-24 lg:py-32 bg-[#2A2420] text-[#FAF4EA] overflow-hidden">
      {/* Rolling marquee banner */}
      <div className="absolute top-0 inset-x-0 overflow-hidden border-b border-white/10 py-4">
        <div className="animate-marquee whitespace-nowrap flex">
          {Array.from({ length: 2 }).map((_, i) => (
            <span key={i} className="flex items-center">
              {marqueeWords.map((tag) => (
                <span key={tag} className="font-serif-alt italic text-2xl text-[#F0D9A7]/40 mx-8">
                  {tag} <span className="text-[#9C3B2E] mx-2">•</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-16">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <span className="inline-flex items-center gap-2 text-[#F0D9A7] text-xs uppercase tracking-[0.35em] mb-5">
            <Camera className="w-4 h-4" /> {t("nav.gallery")}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl font-700 leading-tight">
            {t("gallery.title1")} <span className="italic text-[#F0D9A7]">Quattro Tavoli</span>
          </h2>
        </div>

        {/* Masonry-style grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[180px] sm:auto-rows-[220px]">
          {gallery.map((fileName, i) => (
            <figure
              key={`${fileName}-${i}`}
              className={`reveal group relative overflow-hidden rounded-2xl ${spans[i] || ""}`}
              style={{ transitionDelay: `${(i % 4) * 80}ms` }}
            >
              <GalleryImage
                fileName={fileName}
                alt={`Galerie ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-[#2A2420]/0 group-hover:bg-[#2A2420]/30 transition-colors duration-500" />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
